'use client';

import { Accessibility, Captions, Eye, Keyboard, SlidersHorizontal, Volume2, ArrowLeft } from 'lucide-react';
import Link from 'next/link';

export default function AccessibilityPage() {
 return (
 <div className="min-h-screen bg-background text-ink-secondary font-sans selection:bg-blue-500/30">
 <div className="p-8 pt-16">
 {/* Back Link */}
 <Link
 href="/"
 className="inline-flex items-center gap-2 text-ink-muted hover:text-purple-400 transition-colors mb-8"
 >
 <ArrowLeft size={16} />
 Back to Home
 </Link>

 {/* Header */}
 <div className="mb-8">
 <div className="flex items-center gap-3 mb-2">
 <Accessibility className="h-8 w-8 text-purple-400"/>
 <h1 className="text-3xl font-bold text-ink">Accessibility</h1>
 </div>
 <p className="text-ink-muted mt-1">Our commitment to making this platform accessible to everyone</p>
 </div>

 {/* Accessibility Features Cards */}
 <div className="grid gap-4 md:grid-cols-2">
 <div className="border border-edge rounded-lg bg-surface/50 p-6 hover:border-edge-strong transition-colors">
 <div className="w-12 h-12 bg-purple-500/10 rounded-lg flex items-center justify-center mb-4">
 <Keyboard className="h-6 w-6 text-purple-400"/>
 </div>
 <h3 className="text-lg font-semibold text-ink mb-2">Keyboard Navigation</h3>
 <p className="text-ink-muted text-sm mb-4">
 EngageIQ can be used with a keyboard alone, with a visible focus indicator. A scripted keyboard-only walkthrough of the core workflows (sign-in, joining and running a call, the officer console and administration) was completed in October 2026.
 </p>
 <span className="text-purple-400 text-sm font-medium inline-flex items-center gap-1">
 Available
 </span>
 </div>

 <div className="border border-edge rounded-lg bg-surface/50 p-6 hover:border-edge-strong transition-colors">
 <div className="w-12 h-12 bg-blue-500/10 rounded-lg flex items-center justify-center mb-4">
 <Eye className="h-6 w-6 text-blue-400"/>
 </div>
 <h3 className="text-lg font-semibold text-ink mb-2">Screen Reader Support</h3>
 <p className="text-ink-muted text-sm mb-4">
 Buttons, form fields, tables and charts carry names and structure for screen readers. Testing with screen readers (NVDA and VoiceOver) is still to be done.
 </p>
 <span className="text-blue-400 text-sm font-medium inline-flex items-center gap-1">
 In testing
 </span>
 </div>

 <div className="border border-edge rounded-lg bg-surface/50 p-6 hover:border-edge-strong transition-colors">
 <div className="w-12 h-12 bg-emerald-500/10 rounded-lg flex items-center justify-center mb-4">
 <Volume2 className="h-6 w-6 text-emerald-400"/>
 </div>
 <h3 className="text-lg font-semibold text-ink mb-2">Audio Descriptions</h3>
 <p className="text-ink-muted text-sm mb-4">
 Audio descriptions for visual content and interface elements.
 </p>
 <span className="text-emerald-400 text-sm font-medium inline-flex items-center gap-1">
 Planned
 </span>
 </div>

 <div className="border border-edge rounded-lg bg-surface/50 p-6 hover:border-edge-strong transition-colors">
 <div className="w-12 h-12 bg-amber-500/10 rounded-lg flex items-center justify-center mb-4">
 <Accessibility className="h-6 w-6 text-amber-400"/>
 </div>
 <h3 className="text-lg font-semibold text-ink mb-2">WCAG 2.0 Level AA</h3>
 <p className="text-ink-muted text-sm mb-4">
 EngageIQ is tested against WCAG 2.0 Level A and AA: automated scans of the product&apos;s screens, including live calls, and a keyboard walkthrough of the core workflows (October 2026).{' '}
 <span>Issues found are fixed and retested.</span>{' '}
 Manual screen-reader testing is still to be done.
 </p>
 <span className="text-amber-400 text-sm font-medium inline-flex items-center gap-1">
 Tested
 </span>
 </div>

 <div className="border border-edge rounded-lg bg-surface/50 p-6 hover:border-edge-strong transition-colors">
 <div className="w-12 h-12 bg-purple-500/10 rounded-lg flex items-center justify-center mb-4">
 <SlidersHorizontal className="h-6 w-6 text-purple-400"/>
 </div>
 <h3 className="text-lg font-semibold text-ink mb-2">Accessible Mode</h3>
 <p className="text-ink-muted text-sm mb-4">
 An optional per-user setting adds higher contrast, an always-visible focus indicator, larger touch targets, reduced motion and persistent field labels. With it off, the standard appearance is unchanged.
 </p>
 <span className="text-purple-400 text-sm font-medium inline-flex items-center gap-1">
 Available
 </span>
 </div>

 <div className="border border-edge rounded-lg bg-surface/50 p-6 hover:border-edge-strong transition-colors">
 <div className="w-12 h-12 bg-blue-500/10 rounded-lg flex items-center justify-center mb-4">
 <Captions className="h-6 w-6 text-blue-400"/>
 </div>
 <h3 className="text-lg font-semibold text-ink mb-2">Captions and Transcripts</h3>
 <p className="text-ink-muted text-sm mb-4">
 Live calls show captions, and recorded calls offer captions and a transcript.
 </p>
 <span className="text-blue-400 text-sm font-medium inline-flex items-center gap-1">
 Available
 </span>
 </div>
 </div>

 {/* Statement Section */}
 <div className="mt-8 border border-edge rounded-lg bg-surface/50 p-8 text-center">
 <h2 className="text-xl font-semibold text-ink mb-2">Accessibility Statement</h2>
 <p className="text-ink-muted max-w-2xl mx-auto">
 We are committed to ensuring digital accessibility for people with disabilities.
 We are continually improving the user experience for everyone and applying
 relevant accessibility standards. Contact your administrator if you encounter
 any accessibility barriers.
 </p>
 </div>
 </div>
 </div>
 );
}
